/* eslint-disable react/no-danger */
import React, { useMemo, useState, useContext } from 'react';
import ReactPlayer from 'react-player/lazy';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon } from '@fluentui/react/lib/Icon';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useClientContext, useIsSmartPageVideoSectionEnabled } from '@bingads-webui-campaign-react/smart-page-context';
import { withErrorView } from '@bingads-webui-campaign-react/smart-page-error-boundary';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Loading } from '@bingads-webui-campaign-react/smart-component-primitive';

import { dataProperties } from './data-config';

const getStyles = (components = {}, { palette = {} }) => {
  const container = {
    ...components.section,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  return {
    container,
    mobileContainer: {
      ...container,
      marginLeft: 0,
      marginRight: 0,
    },
    videoContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    videoPlaceholderContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#000000',
      color: '#FFFFFF',
    },
    placeholderText: {
      display: 'flex',
    },
    placeholderIcon: {
      fontSize: '54px',
    },
    invalidVideoIcon: {
      marginRight: '5px',
      fontSize: '16px',
    },
    videoDescription: {
      display: '-webkit-box',
      '-webkit-line-clamp': 10,
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      fontSize: 18,
      lineHeight: '28px',
      color: palette.secondaryText,
      margin: '16px 50px 0px 50px',
      wordBreak: 'break-word',
    },
    mobileVideoDescription: {
      '-webkit-line-clamp': 20,
    },
    loading: {
      position: 'absolute',
      top: 0,
    },
  };
};

export const UnStyledSmartVideoView = ({
  classes,
  videoUrl,
  width,
  height,
  videoDescription,
  id,
  isEditMode,
  videoCanPlay,
}) => {
  const i18n = useContext(I18nContext);
  const { isMobile } = useClientContext();
  const [isReady, setIsReady] = useState(false);
  const [readyUrl, setReadyUrl] = useState('');
  const [videoAvaliable, setVideoAvaliable] = useState(true);

  // only change url when videoCanPlay is true
  if (videoCanPlay && readyUrl !== videoUrl) {
    setVideoAvaliable(true);
    setIsReady(false);
    setReadyUrl(videoUrl);
  }

  const embedVideo = useMemo(() => {
    if (videoCanPlay && videoAvaliable) {
      return (
        <div
          className={classNames('smart-page-embed-video', classes.videoContainer)}
          data-property={dataProperties.videoUrl}
        >
          <ReactPlayer
            url={readyUrl}
            width={width}
            height={height}
            onReady={() => setIsReady(true)}
            onError={() => setVideoAvaliable(false)}
            controls
            disableDeferredLoading
          />
          {
            !isReady && <Loading className={classes.loading} />
          }
        </div>
      );
    } else if (isEditMode || !videoAvaliable) {
      return (
        <div
          className={classNames('smart-page-embed-video-placeholder', classes.videoPlaceholderContainer)}
          data-property={dataProperties.videoUrl}
          style={{ width, height }}
        >
          {videoUrl && (
            <div className={classes.placeholderText}>
              <Icon iconName="Info" className={classes.invalidVideoIcon} />
              {
                videoAvaliable ?
                  i18n.getString(_TL_('Invalid video link: Please make sure your YouTube or Vimeo video is working and try again.')) :
                  i18n.getString(_TL_('Failed to load video. Please check your network.'))
              }
            </div>)}
          {!videoUrl && (<Icon iconName="MSNVideos" className={classes.placeholderIcon} />)}
        </div>
      );
    }

    return null;
  }, [
    videoCanPlay,
    isEditMode,
    classes,
    readyUrl,
    width,
    height,
    i18n,
    videoUrl,
    isReady,
    videoAvaliable,
  ]);

  const description = useMemo(() => {
    if (videoDescription && (videoCanPlay || isEditMode)) {
      return (
        <div
          className={classNames('smart-page-embed-video-description', classes.videoDescription, isMobile ? classes.mobileVideoDescription : '')}
          data-property={dataProperties.videoDescription}
        >
          {videoDescription}
        </div>
      );
    }

    return null;
  }, [classes, isEditMode, videoCanPlay, videoDescription, isMobile]);

  const isSmartPageVideoSectionEnabled = useIsSmartPageVideoSectionEnabled();
  if (!isSmartPageVideoSectionEnabled) {
    return null;
  }

  return !embedVideo ? null :
    (
      <div
        className={classNames('smart-page-embed-video-section', isMobile ? classes.mobileContainer : classes.container)}
        id={id}
      >
        {embedVideo}
        {description}
      </div>
    );
};

UnStyledSmartVideoView.propTypes = {
  classes: PropTypes.shape({
    container: PropTypes.string.isRequired,
  }).isRequired,
  videoUrl: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  videoDescription: PropTypes.string,
  id: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  videoCanPlay: PropTypes.bool,
};

UnStyledSmartVideoView.defaultProps = {
  width: '640px',
  height: '360px',
  videoUrl: '',
  videoDescription: '',
  isEditMode: false,
  videoCanPlay: false,
};

export const SmartVideoView = withErrorView(withDefaultStyles(
  UnStyledSmartVideoView,
  getStyles
), 'componentMode');
